import { useCallback, useState } from "react";

import { QrCode } from "../components/QrCode";
import { useSession } from "../useSession";

/**
 * Where the patient app's registration link lives.
 *
 * The same address clinic_qr.py writes into the printed posters, so a poster
 * printed from this screen and one generated on the command line carry the
 * same code. Overridable for a staging build, which has its own app.
 */
/**
 * The marketing site, not a separate app host.
 *
 * This pointed at app.healtrics.com, which does not exist: no DNS record, and
 * giving it one needs a reissued certificate, because an ACM certificate
 * cannot gain a name after it is issued. Meanwhile a patient scanning a
 * poster with an ordinary phone camera got a dead link, which is the one
 * moment the poster exists for.
 *
 * healtrics.com already resolves, already serves `/register`, and already
 * deploys on a push. A nicer hostname can come later and change nothing but
 * this line.
 */
const APP_URL = (
  import.meta.env.VITE_APP_URL ?? "https://healtrics.com"
).replace(/\/$/, "");

/**
 * The poster for the front desk.
 *
 * A clinic cannot ask anybody to run a management command, and until now that
 * was the only way to get its QR: shell access to the deployed backend, which
 * no clinic has and none should.
 *
 * It is drawn from the clinic already in the session, so there is no endpoint
 * and nothing to ask for. The id is not a secret. It is printed on a poster at
 * the front desk by design, which is also why the code is written out
 * underneath: a patient whose camera will not focus types it instead, and the
 * app takes either.
 *
 * The page prints. That is the whole point of it, so the print rules are part
 * of the screen rather than an afterthought: the navigation, the buttons and
 * the explanation all go, and the poster keeps the sheet to itself.
 */
export function ClinicPoster() {
  const { clinic } = useSession();
  const [image, setImage] = useState<string | null>(null);
  const keep = useCallback((dataUrl: string) => setImage(dataUrl), []);

  if (!clinic) return <p className="empty">Pick a clinic first.</p>;

  const link = `${APP_URL}/register?clinic=${clinic.id}`;
  const slug = clinic.name.toLowerCase().replace(/[^a-z0-9]+/g, "-");

  return (
    <section className="poster-page">
      <div className="poster-tools">
        <h1>Your clinic's poster</h1>
        <p className="lead">
          Print this and put it where patients wait. Scanning it opens the app
          already pointed at {clinic.name}, so nobody has to type anything.
        </p>
        <div className="poster-actions">
          <button type="button" onClick={() => window.print()}>
            Print
          </button>
          {image && (
            <a className="button quiet" href={image} download={`${slug}-healtrics.png`}>
              Download the code
            </a>
          )}
        </div>
        <p className="poster-note">
          The code below is the same one the app asks for under <strong>Enter
          the code instead</strong>. It is not a password: it names the clinic
          and nothing else, and a patient still signs in with a code sent to
          their own email.
        </p>
      </div>

      {/* The sheet. Everything above is for the person at the desk; this is
          what a patient reads from two metres away. */}
      <article className="poster">
        <p className="poster-brand">Healtrics</p>
        <h2 className="poster-clinic">{clinic.name}</h2>
        <p className="poster-what">Your records, your appointments, on your phone.</p>

        <QrCode
          value={link}
          alt={`Registration code for ${clinic.name}`}
          width={420}
          onReady={keep}
        />

        <p className="poster-how">Point your camera at the code.</p>

        <div className="poster-manual">
          <p className="poster-manual-head">No camera? Open the app and tap <strong>Enter the code instead</strong>:</p>
          <p className="poster-code">{clinic.id}</p>
        </div>
      </article>
    </section>
  );
}
